import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { auth, api, getStoredUser } from "../lib/api";
import { Loader2, Swords, Check, X } from "lucide-react";

function errText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const WagerSignupModal = ({ open, onClose, onDone, title = "Create your manager", subtitle = "Quick sign-up so mates can find and challenge you. Takes 10 seconds.", locked = false, cta = "Create & continue" }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // live-check state
  const [uState, setUState] = useState(null); // {available, suggestions} | 'checking' | null
  const [eState, setEState] = useState(null); // {valid, available} | 'checking' | null
  const uTimer = useRef(null);
  const eTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setUsername(""); setEmail(""); setPassword(""); setError(""); setUState(null); setEState(null);
    }
  }, [open]);

  useEffect(() => () => { clearTimeout(uTimer.current); clearTimeout(eTimer.current); }, []);

  const onUsername = (v) => {
    setUsername(v);
    setUState(v.trim().length >= 2 ? "checking" : null);
    clearTimeout(uTimer.current);
    if (v.trim().length < 2) return;
    uTimer.current = setTimeout(async () => {
      try {
        const r = await api.usernameAvailable(v.trim());
        setUState(r);
      } catch (e) { setUState(null); }
    }, 450);
  };

  const onEmail = (v) => {
    setEmail(v);
    clearTimeout(eTimer.current);
    if (!EMAIL_RE.test(v.trim())) {
      setEState(v.trim() ? { valid: false, available: false } : null);
      return;
    }
    setEState("checking");
    eTimer.current = setTimeout(async () => {
      try {
        const r = await api.emailAvailable(v.trim());
        setEState(r);
      } catch (e) { setEState(null); }
    }, 450);
  };

  const usernameBad = uState && uState !== "checking" && !uState.available;
  const emailBad = eState && eState !== "checking" && (!eState.valid || !eState.available);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!username.trim()) return setError("Pick a username.");
    if (usernameBad) return setError("That username is taken — try a suggestion below.");
    if (!EMAIL_RE.test(email.trim())) return setError("That email doesn't look right.");
    if (eState && eState !== "checking" && eState.valid && !eState.available) return setError("That email's already in use — try logging in.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setBusy(true);
    try {
      const payload = { email: email.trim(), password, username: username.trim() };
      const u = getStoredUser()?.is_guest ? await auth.upgrade(payload) : await auth.register(payload);
      onDone(u);
    } catch (err) {
      setError(errText(err?.response?.data?.detail) || "Couldn't create your account.");
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = (bad, good) =>
    `w-full rounded-xl bg-black/25 border px-3.5 py-3 text-sm outline-none placeholder:text-muted-38 ${
      bad ? "border-[#EF4444]/60" : good ? "border-[#10B981]/60" : "border-white/10 focus:border-[#10B981]/50"
    }`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !locked && onClose()}>
      <DialogContent
        data-testid="wager-signup-modal"
        className={`theme-light w-[calc(100%-2rem)] bg-[#1E293B] border border-[#10B981]/30 text-white max-w-sm p-6 ${locked ? "[&>button]:hidden" : ""}`}
        onInteractOutside={(e) => locked && e.preventDefault()}
        onEscapeKeyDown={(e) => locked && e.preventDefault()}
      >
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mb-1">
          <Swords className="w-7 h-7 text-[#10B981]" />
        </div>
        <DialogTitle className="font-display font-extrabold text-2xl text-center">{title}</DialogTitle>
        <DialogDescription className="text-muted-38 text-sm text-center mt-1">{subtitle}</DialogDescription>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <input
              data-testid="wager-signup-username"
              value={username}
              onChange={(e) => onUsername(e.target.value)}
              placeholder="Username (your manager name)"
              maxLength={20}
              className={fieldCls(usernameBad, uState && uState.available)}
            />
            {uState === "checking" && <p className="text-muted-38 text-xs mt-1.5">Checking…</p>}
            {uState && uState !== "checking" && uState.available && (
              <p className="text-[#10B981] text-xs mt-1.5 flex items-center gap-1"><Check className="w-3 h-3" /> Available</p>
            )}
            {usernameBad && (
              <div className="mt-1.5">
                <p className="text-[#EF4444] text-xs flex items-center gap-1"><X className="w-3 h-3" /> That username is taken</p>
                {uState.suggestions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="username-suggestions">
                    {uState.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid="username-suggestion"
                        onClick={() => onUsername(s)}
                        className="rounded-full bg-[#10B981]/12 border border-[#10B981]/30 text-[#10B981] text-xs px-2.5 py-1 hover:bg-[#10B981]/20 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <input
              data-testid="wager-signup-email"
              type="email"
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              placeholder="Email"
              className={fieldCls(emailBad, eState && eState !== "checking" && eState.valid && eState.available)}
            />
            {eState === "checking" && <p className="text-muted-38 text-xs mt-1.5">Checking…</p>}
            {eState && eState !== "checking" && !eState.valid && (
              <p data-testid="email-invalid" className="text-[#EF4444] text-xs mt-1.5 flex items-center gap-1"><X className="w-3 h-3" /> That email doesn't look right</p>
            )}
            {eState && eState !== "checking" && eState.valid && !eState.available && (
              <p data-testid="email-taken" className="text-[#EF4444] text-xs mt-1.5 flex items-center gap-1"><X className="w-3 h-3" /> That email's already in use — try logging in</p>
            )}
            {eState && eState !== "checking" && eState.valid && eState.available && (
              <p className="text-[#10B981] text-xs mt-1.5 flex items-center gap-1"><Check className="w-3 h-3" /> Looks good</p>
            )}
          </div>

          <input
            data-testid="wager-signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (6+ characters)"
            className={fieldCls(false, false)}
          />
          {error && <p data-testid="wager-signup-error" className="text-[#EF4444] text-sm">{error}</p>}
          <button
            data-testid="wager-signup-submit"
            type="submit"
            disabled={busy || uState === "checking" || eState === "checking" || usernameBad || emailBad}
            className="btn-green w-full rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WagerSignupModal;
